<script setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const starMapStore = useCustomStarMapStore();

const mapId = computed(() => String(route.params.mapId || ""));
const shareCode = computed(() => String(route.query.shareCode || ""));
const map = computed(() => starMapStore.getMap(mapId.value));

onMounted(async () => {
  try {
    if (starMapStore.backendEnabled) {
      if (shareCode.value) {
        await starMapStore.loadSharedRemoteMap(shareCode.value);
      } else {
        await starMapStore.loadRemoteMap(mapId.value);
      }

      await starMapStore.loadRemoteProgress(
        mapId.value,
        { shareCode: shareCode.value }
      );
    } else {
      starMapStore.refresh();
    }
  } catch {
    // Keep cached completed-map content visible if refresh is temporarily unavailable.
  }

  if (!map.value) {
    await router.replace({ name: "home" });
  }
});
</script>

<template>
  <section v-if="map" class="complete-page">
    <div class="complete-card">
      <div class="complete-stars" aria-hidden="true">
        <span>✦</span><span>✧</span><span>✦</span>
      </div>
      <h1>{{ languageStore.t('customMapCompleteTitle') }}</h1>
      <p>{{ languageStore.t('customMapCompleteSubtitle', { name: map.name }) }}</p>
      <div class="complete-actions">
        <button class="neon-button" type="button" @click="router.replace({ name: 'custom-map-view', params: { mapId }, query: { from: 'complete', ...(shareCode ? { shareCode, mode: 'play' } : {}) } })">
          {{ languageStore.t('viewCompletedMap') }}
        </button>
        <button class="neon-button secondary" type="button" @click="router.replace({ name: 'home' })">
          {{ languageStore.t('backHome') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.complete-page { min-height:100dvh; display:grid; place-items:center; padding:24px; background:radial-gradient(circle at 50% 35%,rgba(82,246,232,.12),transparent 32%),#030914; }
.complete-card { width:min(92vw,520px); padding:36px 28px 28px; text-align:center; border:1px solid rgba(82,246,232,.44); border-radius:24px; background:rgba(5,25,40,.94); box-shadow:0 22px 70px rgba(0,0,0,.55); }
.complete-stars { display:flex; justify-content:center; align-items:center; gap:18px; margin-bottom:14px; color:#8ffff6; }
.complete-stars span { font-size:28px; text-shadow:0 0 10px #8ffff6,0 0 22px rgba(82,246,232,.7); animation:completeTwinkle 1.25s ease-in-out infinite; }
.complete-stars span:nth-child(2){font-size:18px;animation-delay:.22s}.complete-stars span:nth-child(3){animation-delay:.44s}
@keyframes completeTwinkle{0%,100%{transform:scale(.78);opacity:.5}50%{transform:scale(1.2);opacity:1}}
.complete-card h1 { margin:0; color:#fff; font-size:clamp(28px,7vw,44px); font-weight:950; }
.complete-card p { margin:12px auto 24px; color:rgba(255,255,255,.68); line-height:1.65; font-size:13px; }
.complete-actions { display:grid; gap:10px; }
.complete-actions .neon-button { width:100%; min-height:48px; }
</style>
